import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import * as z from "zod/v4";
import { api } from "../http";
import { roleSchema } from "@/lib/queries/roles";
import { departmentSchema } from "@/lib/queries/departments";
import { paginatedListSchema } from "@/lib/utils/zod";

export const userFormSchema = z.object({
  full_name: z.string().min(1, "Full name is required"),
  email: z.email("Valid email is required"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  is_active: z.boolean().default(true),
  role_id: z.number().min(1, "Role is required"),
  department_id: z.number().min(1, "Department is required"),
});

export const userUpdateFormSchema = userFormSchema.extend({
  password: z
    .string()
    .min(6, "Password must be at least 6 characters")
    .optional(),
});

export const userSchema = userFormSchema.extend({
  id: z.string(),
  created_at: z.string(),
  updated_at: z.string().nullable(),
  role: roleSchema.optional(),
  department: departmentSchema.optional(),
});

export const usersListSchema = paginatedListSchema(userSchema);

export const userPolicyAssociationSchema = z.object({
  id: z.number(),
  name: z.string(),
  priority: z.number(),
  type: z.string(),
  enforcement_mode: z.string(),
  is_pre_ready: z.boolean(),
  is_active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string().nullable(),
  added_at: z.string(),
});

export const userPoliciesListSchema = z.array(userPolicyAssociationSchema);

export type UserFormData = z.infer<typeof userFormSchema>;
export type UserUpdateFormData = z.infer<typeof userUpdateFormSchema>;
export type User = z.infer<typeof userSchema>;
export type UsersList = z.infer<typeof usersListSchema>;
export type UserPolicyAssociation = z.infer<typeof userPolicyAssociationSchema>;
export type UserPoliciesList = z.infer<typeof userPoliciesListSchema>;

type SearchParams = {
  page: number;
  per_page?: number;
  search?: string;
  role_id?: number;
  department_id?: number;
  is_active?: boolean;
};

export function usersListQueryOptions(params: SearchParams) {
  return queryOptions({
    queryKey: ["users", "list", params],
    async queryFn({ client }) {
      const searchParams = new URLSearchParams({
        page: params.page.toString(),
        per_page: (params.per_page ?? 10).toString(),
      });

      if (params.search) searchParams.set("search", params.search);
      if (params.role_id !== undefined)
        searchParams.set("role_id", params.role_id.toString());
      if (params.department_id !== undefined)
        searchParams.set("department_id", params.department_id.toString());
      if (params.is_active !== undefined)
        searchParams.set("is_active", params.is_active.toString());

      const users = await api.raw
        .get("api/v1/users/", { searchParams })
        .json<UsersList>();

      users.items.map((user) =>
        client.setQueryData(["users", "detail", user.id], user),
      );

      return users;
    },
  });
}

export function userQueryOptions(id: string) {
  return queryOptions({
    queryKey: ["users", "detail", id],
    queryFn: () => api.raw.get(`api/v1/users/${id}`).json<User>(),
  });
}

export function useUsersList(params: SearchParams) {
  return useQuery(usersListQueryOptions(params));
}

export function useUser(id: string) {
  return useQuery(userQueryOptions(id));
}

export function userPoliciesListQueryOptions(userId: string) {
  return queryOptions({
    queryKey: ["users", userId, "policies", "list"],
    queryFn: async () => {
      return api.raw
        .get(`api/v1/users/${userId}/policies`)
        .json<UserPoliciesList>();
    },
  });
}

export function useUserPoliciesList(userId: string) {
  return useQuery(userPoliciesListQueryOptions(userId));
}

export function useCreateUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UserFormData) => {
      const response = await api.raw
        .post("api/v1/users/", { json: data })
        .json<User>();
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users", "list"] });
    },
  });
}

export function useUpdateUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UserUpdateFormData;
    }) => {
      const response = await api.raw
        .put(`api/v1/users/${id}/`, { json: data })
        .json<User>();
      return response;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["users", "list"] });
      queryClient.invalidateQueries({
        queryKey: ["users", "detail", variables.id],
      });
    },
  });
}

export function useDeleteUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.raw.delete(`api/v1/users/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users", "list"] });
    },
  });
}
